import { aiSettingsToAiReplyConfig } from "@redotech/effect-api-shared/default-rules/support-settings-mapping";
import { t } from "@redotech/redo-i18n";
import {
  SUPPORT_INSIGHT_SLOT_COUNT,
  SupportInsightActionId,
  teamsPageBannerLocationId,
  type SupportInsightMetricLocationId,
  type SupportInsightSlot,
} from "@redotech/redo-support-settings-common/support-insight-slots";
import { supportInsightsEnabled } from "@redotech/redo-support-settings-common/support-settings";
import {
  defaultInsightSlots,
  qualifySupportInsights,
  resolveInsightSlots,
  socialChannelGaps,
  type SupportInsightSignals,
} from "./qualify-support-insights";

/** Same rep and salary assumptions as the Support dashboard cost-savings card. */
const ANNUAL_SALARY = 40_150;
const WEEKS_PER_YEAR = 48;
const TICKETS_PER_DAY = 60;
const WORK_DAYS_PER_WEEK = 5;
const HOURS_PER_WORK_WEEK = 40;
const TICKETS_PER_REP_WEEK = TICKETS_PER_DAY * WORK_DAYS_PER_WEEK;
const COST_PER_TICKET = ANNUAL_SALARY / (WEEKS_PER_YEAR * TICKETS_PER_REP_WEEK);
const HOURS_PER_TICKET = HOURS_PER_WORK_WEEK / TICKETS_PER_REP_WEEK;

/** Same channels the email toggle adds in `aiSettingsToAiReplyConfig`. */
const EMAIL_AI_CHANNELS = aiSettingsToAiReplyConfig({
  autopilotEnabled: true,
  enabledPlatforms: { email: true },
}).configurations.channels;

/** Ticket totals for one merchant over `windowDays`. */
export interface SupportInsightVolume {
  windowDays: number;
  aiResolved: number;
  humanResolved: number;
  aiMedianResolutionMs: number;
  humanMedianResolutionMs: number;
  channels: readonly { channel: string; count: number }[];
}

export interface SupportInsightFigures {
  extraHoursPerWeek: number;
  extraDollarsPerYear: number;
  extraHoursPerMonth: number;
  percentFaster: number;
}

const ZERO_FIGURES: SupportInsightFigures = {
  extraHoursPerWeek: 0,
  extraDollarsPerYear: 0,
  extraHoursPerMonth: 0,
  percentFaster: 0,
};

function channelCount(
  volume: SupportInsightVolume,
  names: readonly string[],
): number {
  return volume.channels.reduce(
    (sum, row) => (names.includes(row.channel) ? sum + row.count : sum),
    0,
  );
}

/** Share of assigned tickets AI resolves, capped at 1. */
function automationRate(volume: SupportInsightVolume): number {
  const total = volume.aiResolved + volume.humanResolved;
  if (total <= 0) {
    return 0;
  }
  return volume.aiResolved / total;
}

function extraTickets(
  id: SupportInsightActionId,
  signals: SupportInsightSignals,
  volume: SupportInsightVolume,
  automationRateOverride?: number,
): number {
  const rate =
    automationRateOverride !== undefined
      ? automationRateOverride
      : automationRate(volume);
  switch (id) {
    case "ai-ticket-volume":
      return 0;
    case "email-channel":
      return channelCount(volume, EMAIL_AI_CHANNELS) * rate;
    case "widget-ai":
      return channelCount(volume, ["redoChat"]) * rate;
    case "widget-enabled":
      return 0;
    case "social-ai":
      return (
        socialChannelGaps(signals).reduce(
          (count, gap) => count + channelCount(volume, [gap.channel]),
          0,
        ) * rate
      );
  }
}

function scale(count: number, windowDays: number, days: number): number {
  if (windowDays <= 0) {
    return 0;
  }
  return (count * days) / windowDays;
}

export function supportInsightFigures(
  id: SupportInsightActionId,
  signals: SupportInsightSignals,
  volume: SupportInsightVolume,
  options?: { automationRate?: number },
): SupportInsightFigures {
  if (id === "ai-ticket-volume" || volume.windowDays <= 0) {
    return ZERO_FIGURES;
  }
  const extra = extraTickets(id, signals, volume, options?.automationRate);
  const weeklyExtra = scale(extra, volume.windowDays, 7);
  const extraHoursPerWeek = weeklyExtra * HOURS_PER_TICKET;
  const human = volume.humanMedianResolutionMs;
  const ai = volume.aiMedianResolutionMs;
  const percentFaster =
    volume.aiResolved > 0 && human > 0 && ai >= 0 && ai < human
      ? ((human - ai) / human) * 100
      : 0;
  return {
    extraHoursPerWeek,
    extraDollarsPerYear: weeklyExtra * WEEKS_PER_YEAR * COST_PER_TICKET,
    extraHoursPerMonth: extraHoursPerWeek * (365 / 12 / 7),
    percentFaster,
  };
}

export function formatInsightHours(hours: number): string {
  if (!Number.isFinite(hours) || hours <= 0) {
    return "0";
  }
  const rounded = hours >= 10 ? Math.round(hours) : Math.round(hours * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

export function formatInsightDollars(dollars: number): string {
  const amount = Number.isFinite(dollars)
    ? Math.max(0, Math.round(dollars))
    : 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatInsightPercent(percent: number): string {
  if (!Number.isFinite(percent) || percent <= 0) {
    return "0";
  }
  return String(Math.round(percent));
}

/** Metric cards and the Teams page banner that have official insight copy. */
export type SupportInsightMessageLocation =
  | SupportInsightMetricLocationId
  | typeof teamsPageBannerLocationId;

const supportInsightCopyLocations = new Set<SupportInsightMessageLocation>([
  "ticket-automation-rate",
  "total-tickets-automated",
  "potential-cost-savings",
  "median-time-to-resolution",
  teamsPageBannerLocationId,
]);

export function supportInsightLocationHasCopy(
  location: SupportInsightMessageLocation,
): boolean {
  return supportInsightCopyLocations.has(location);
}

const networkList = new Intl.ListFormat("en", {
  style: "long",
  type: "conjunction",
});

interface InsightActionPhrase {
  taking: string;
  turnOn: string;
}

function socialNetworkNames(signals: SupportInsightSignals): string {
  const networks: string[] = [];
  for (const gap of socialChannelGaps(signals)) {
    if (!networks.includes(gap.network)) {
      networks.push(gap.network);
    }
  }
  return networkList.format(networks) || "Facebook and Instagram";
}

/** Current setup, then the action and the hours it would give back. */
function teamsPageBannerCopy(
  id: SupportInsightActionId,
  signals: SupportInsightSignals,
  figures: SupportInsightFigures,
): string {
  const action = bannerSentence(
    id,
    signals,
    formatInsightHours(figures.extraHoursPerMonth),
  );
  return `${currentSetupSentence(id, signals)} ${action}`;
}

function currentSetupSentence(
  id: SupportInsightActionId,
  signals: SupportInsightSignals,
): string {
  switch (id) {
    case "ai-ticket-volume":
      return "";
    case "email-channel":
      return t("Right now, your team handles every email.");
    case "widget-ai":
      return t("Right now, your team answers every chat.");
    case "widget-enabled":
      return t("Right now, shoppers have no chat widget on your store.");
    case "social-ai":
      return t("Right now, your team handles {{networks}} without AI.", {
        networks: socialNetworkNames(signals),
      });
  }
}

function bannerSentence(
  id: SupportInsightActionId,
  signals: SupportInsightSignals,
  hours: string,
): string {
  const networks = id === "social-ai" ? socialNetworkNames(signals) : "";
  if (hours === "0") {
    return bannerActionOnly(id, networks);
  }
  if (hours === "1") {
    return bannerActionOneHour(id, networks, hours);
  }
  return bannerActionHours(id, networks, hours);
}

function bannerActionOnly(
  id: SupportInsightActionId,
  networks: string,
): string {
  switch (id) {
    case "ai-ticket-volume":
      return "";
    case "email-channel":
      return t("Turn on AI for email.");
    case "widget-ai":
      return t("Turn on AI for the chat widget.");
    case "widget-enabled":
      return t("Turn on the chat widget.");
    case "social-ai":
      return t("Turn on AI for {{networks}}.", { networks });
  }
}

function bannerActionOneHour(
  id: SupportInsightActionId,
  networks: string,
  hours: string,
): string {
  switch (id) {
    case "ai-ticket-volume":
      return "";
    case "email-channel":
      return t("Turn on AI for email to get back {{hours}} hour a month.", {
        hours,
      });
    case "widget-ai":
      return t(
        "Turn on AI for the chat widget to get back {{hours}} hour a month.",
        { hours },
      );
    case "widget-enabled":
      return t("Turn on the chat widget to get back {{hours}} hour a month.", {
        hours,
      });
    case "social-ai":
      return t(
        "Turn on AI for {{networks}} to get back {{hours}} hour a month.",
        { networks, hours },
      );
  }
}

function bannerActionHours(
  id: SupportInsightActionId,
  networks: string,
  hours: string,
): string {
  switch (id) {
    case "ai-ticket-volume":
      return "";
    case "email-channel":
      return t("Turn on AI for email to get back {{hours}} hours a month.", {
        hours,
      });
    case "widget-ai":
      return t(
        "Turn on AI for the chat widget to get back {{hours}} hours a month.",
        { hours },
      );
    case "widget-enabled":
      return t("Turn on the chat widget to get back {{hours}} hours a month.", {
        hours,
      });
    case "social-ai":
      return t(
        "Turn on AI for {{networks}} to get back {{hours}} hours a month.",
        { networks, hours },
      );
  }
}

function insightActionPhrase(
  id: SupportInsightActionId,
  signals: SupportInsightSignals,
): InsightActionPhrase {
  switch (id) {
    case "ai-ticket-volume":
      return { taking: "", turnOn: "" };
    case "email-channel":
      return { taking: "turning on AI for email", turnOn: "email" };
    case "widget-ai":
      return {
        taking: "turning on AI for the chat widget",
        turnOn: "the chat widget",
      };
    case "widget-enabled":
      return {
        taking: "turning on the chat widget",
        turnOn: "the chat widget",
      };
    case "social-ai": {
      const names = socialNetworkNames(signals);
      return { taking: `turning on AI for ${names}`, turnOn: names };
    }
  }
}

/** Short copy for a location, action, and this merchant's ticket totals. */
export function supportInsightMessage(
  location: SupportInsightMessageLocation,
  id: SupportInsightActionId,
  signals: SupportInsightSignals,
  volume: SupportInsightVolume,
): string | undefined {
  if (id === "ai-ticket-volume") {
    return undefined;
  }
  const action = insightActionPhrase(id, signals);
  const figures = supportInsightFigures(id, signals, volume);
  switch (location) {
    case "ticket-automation-rate":
    case "total-tickets-automated":
      if (id === "widget-enabled") {
        return undefined;
      }
      return `Save +${formatInsightHours(figures.extraHoursPerWeek)} hours/week by ${action.taking}.`;
    case "potential-cost-savings":
      if (id === "widget-enabled") {
        return undefined;
      }
      return `Save +${formatInsightDollars(figures.extraDollarsPerYear)}/yr with AI by ${action.taking}.`;
    case "median-time-to-resolution":
      return `Resolve tickets ${formatInsightPercent(figures.percentFaster)}% faster ${action.taking}.`;
    case teamsPageBannerLocationId:
      return teamsPageBannerCopy(id, signals, figures);
    case "closed-tickets":
    case "created-tickets":
    case "median-first-human-response":
    case "median-first-response-time":
    case "resolved-tickets":
    case "total-tickets":
      return undefined;
  }
}

const TEAMS_PAGE_BANNER_SLOT = SUPPORT_INSIGHT_SLOT_COUNT - 1;

function isSupportInsightActionId(
  value: string,
): value is SupportInsightActionId {
  for (const id of SupportInsightActionId) {
    if (id === value) {
      return true;
    }
  }
  return false;
}

/** Saved insight 4. Undefined when the stored slots are not a full configuration. */
function savedTeamsPageBanner(
  qualified: readonly SupportInsightActionId[],
  insights: readonly SupportInsightSlot[],
): { action: SupportInsightActionId | null; enabled: boolean } | undefined {
  if (insights.length !== SUPPORT_INSIGHT_SLOT_COUNT) {
    return undefined;
  }
  const banner = insights[TEAMS_PAGE_BANNER_SLOT];
  if (!banner || banner.location !== teamsPageBannerLocationId) {
    return undefined;
  }
  const actions = insights.map((slot) =>
    slot.action && isSupportInsightActionId(slot.action) ? slot.action : null,
  );
  return {
    action:
      resolveInsightSlots(qualified, actions)[TEAMS_PAGE_BANNER_SLOT] ?? null,
    enabled: banner.enabled !== false,
  };
}

/** Insight 4's action. Hidden until Support insights are turned on. */
export function teamsPageBannerAction(
  signals: SupportInsightSignals,
  saved?: {
    insightsEnabled?: boolean | null;
    insights?: readonly SupportInsightSlot[] | null;
  } | null,
): SupportInsightActionId | undefined {
  if (!supportInsightsEnabled(saved)) {
    return undefined;
  }
  const qualified = qualifySupportInsights(signals);
  const stored = saved?.insights;
  const banner = stored ? savedTeamsPageBanner(qualified, stored) : undefined;
  if (banner) {
    if (!banner.enabled || !banner.action) {
      return undefined;
    }
    return banner.action;
  }
  return defaultInsightSlots(qualified)[TEAMS_PAGE_BANNER_SLOT] ?? undefined;
}

/** Copy for insight 4. Hidden until Support insights are turned on. */
export function teamsPageBannerMessage(
  signals: SupportInsightSignals,
  volume: SupportInsightVolume,
  saved?: {
    insightsEnabled?: boolean | null;
    insights?: readonly SupportInsightSlot[] | null;
  } | null,
): string | undefined {
  const id = teamsPageBannerAction(signals, saved);
  if (!id) {
    return undefined;
  }
  return supportInsightMessage(teamsPageBannerLocationId, id, signals, volume);
}
